/**
 * Corner ribbon — the rotated -45° DEMO / UPCOMING / EARLY ACCESS banner on
 * Discover gallery tiles (INVENTORY §4.1, prototype template line 133 + rules
 * at lines 1544-1556).
 *
 * The rules, verbatim from the prototype:
 *   UPCOMING and EARLY ACCESS are mutually exclusive (`up` wins).
 *   DEMO always rides ON TOP; whichever of the other two applies tucks
 *   UNDERNEATH at a fixed offset with a dark top-gradient so the overlap reads.
 *   Colours: DEMO `--acc`, UPCOMING `--acc2`, EARLY ACCESS `--gt-info`.
 *   Each solo ribbon has its OWN top/left/width triple — they are not
 *   interchangeable.
 *
 * Usage: render inside the tile's `position:relative;overflow:hidden` box.
 * Renders a fragment (two absolutely-positioned divs at z-index 1 and 2), or
 * nothing when no flag applies — there is no host wrapper, because the
 * prototype inlined this markup rather than importing a component.
 *
 * NOTE: the top ribbon's `box-shadow:0 3px 9px rgba(0,0,0,.65)` is a *blurred*
 * shadow, which `design/DESIGN-CLAUDE.md` bans everywhere else. It is in the
 * frozen prototype, so it is reproduced here; do not "fix" it without a design
 * decision.
 */

interface RibDef {
  t: string;
  bg: string;
  top: string;
  left: string;
  w: string;
}

const DEMO: RibDef = { t: 'DEMO', bg: 'var(--acc)', top: '26px', left: '-48px', w: '170px' };
const UPCOMING: RibDef = { t: 'UPCOMING', bg: 'var(--acc2)', top: '30px', left: '-44px', w: '180px' };
const EARLY: RibDef = { t: 'EARLY ACCESS', bg: 'var(--gt-info)', top: '34px', left: '-42px', w: '190px' };

export interface RibbonProps {
  /** `g.up` — unreleased */
  up?: boolean;
  /** `g.ea` — early access */
  ea?: boolean;
  /** `g.demo` — has a free demo */
  demo?: boolean;
}

export function Ribbon({ up, ea, demo }: RibbonProps) {
  let rib: RibDef | null = null;
  let under: { t: string; bg: string } | null = null;
  if (up) {
    if (demo) {
      rib = DEMO;
      under = { t: UPCOMING.t, bg: UPCOMING.bg };
    } else rib = UPCOMING;
  } else if (ea) {
    if (demo) {
      rib = DEMO;
      under = { t: EARLY.t, bg: EARLY.bg };
    } else rib = EARLY;
  } else if (demo) rib = DEMO;

  if (!rib) return null;
  return (
    <>
      {under ? (
        <div
          style={{
            position: 'absolute',
            top: '33px',
            left: '-46px',
            width: '200px',
            transform: 'rotate(-45deg)',
            background: 'linear-gradient(180deg,rgba(5,5,8,.38),rgba(5,5,8,0) 55%),' + under.bg,
            color: 'var(--gt-ink)',
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: '8px',
            textAlign: 'center',
            padding: '17px 0 8px',
            zIndex: 1,
          }}
        >
          <span>{under.t}</span>
        </div>
      ) : null}
      <div
        style={{
          position: 'absolute',
          top: rib.top,
          left: rib.left,
          width: rib.w,
          transform: 'rotate(-45deg)',
          background: rib.bg,
          color: 'var(--gt-ink)',
          fontFamily: 'var(--gt-font-pixel)',
          fontSize: '8px',
          textAlign: 'center',
          padding: '8px 0',
          boxShadow: '0 3px 9px rgba(0,0,0,.65)',
          zIndex: 2,
        }}
      >
        <span>{rib.t}</span>
      </div>
    </>
  );
}
